import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, MapPin, Clock, Sparkles } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export const Footer = () => {
  const { content } = useContent();
  const s = content?.settings || {};
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#1A1615] text-[#E8D5CE]" data-testid="site-footer">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-16 sm:grid-cols-2 lg:grid-cols-4 lg:py-20">
        <div>
          <div className="flex items-center gap-3">
            <img src={s.logo_url || "/assets/logo-mark.png"} alt={`${s.clinic_name || "Royal Skin Cosmetic Clinic"} logo`} className="h-11 w-auto object-contain" />
            <span className="leading-none">
              <span className="font-serif text-xl font-semibold text-white">{(s.clinic_name || "Royal Skin Cosmetic Clinic").replace(/\s*cosmetic\s*clinic\s*/i, "")}</span>
              <span className="block text-[9px] font-medium tracking-[0.3em] text-[#D4AF37]">COSMETIC CLINIC</span>
            </span>
          </div>
          <p className="mt-5 text-sm font-light leading-relaxed text-[#A39288]">
            {s.tagline || "Luxury Cosmetic Skin Clinic"} — physician-led artistry, medical-grade technology and an
            atmosphere of absolute calm.
          </p>
          {s.instagram && (
            <a
              href={s.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram"
              data-testid="footer-instagram-link"
              className="mt-5 inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"
            >
              <Instagram className="h-5 w-5" />
            </a>
          )}
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Explore</h4>
          <ul className="mt-5 space-y-3 text-sm font-light">
            {[
              ["Treatments", "/treatments"],
              ["Our Doctors", "/doctors"],
              ["About Royal Skin", "/about"],
              ["Results Gallery", "/gallery"],
              ["Skin Journal", "/blog"],
              ["Contact & Booking", "/contact"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="transition-colors hover:text-[#D4AF37]">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Contact</h4>
          <ul className="mt-5 space-y-4 text-sm font-light">
            <li className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <a href={`tel:${(s.phone || "").replace(/[^+\d]/g, "")}`} data-testid="footer-phone-link" className="transition-colors hover:text-[#D4AF37]">
                {s.phone || "+1 (800) 737-3648"}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <a href={`mailto:${s.email || "care@serenityskin.com"}`} data-testid="footer-email-link" className="transition-colors hover:text-[#D4AF37]">
                {s.email || "care@serenityskin.com"}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <span data-testid="footer-address">{s.address || "128 Rosewood Avenue, Beverly Hills, CA 90210"}</span>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Opening Hours</h4>
          <p className="mt-5 flex items-start gap-3 text-sm font-light leading-relaxed">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
            <span data-testid="footer-hours">{s.hours || "Mon – Sat: 9:00 AM – 8:00 PM"}</span>
          </p>
          <Link to="/contact" data-testid="footer-visit-link" className="btn-ghost-lux mt-6 border-[#D4AF37]/40 px-5 py-2.5 text-[13px] text-[#F3E5AB] hover:bg-[#D4AF37]/10">
            Plan Your Visit
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-[#A39288] sm:flex-row">
          <p>© {year} {s.clinic_name || "Serenity Skin"}. All rights reserved.</p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="transition-colors hover:text-[#D4AF37]">
            Backoffice
          </Link>
        </div>
      </div>
    </footer>
  );
};
